<template>
  <div class="column">
    <div class="columns">
      <div class="column">
      <data-table
        :key="tableKey"
        :tableOpts="tableOpts"
        :loadData="loadData"
        :apiMode="true"
        @cell-clicked="onCellClicked"
      />

      <list-item-form
        v-if="showModal"
        :item="itemToEdit"
        :config="modalConfig"
        @close="closeModal"
        @refresh-table="refreshTable"
      />
      </div>
    </div>
  </div>
</template>

<script>
import { reactive, toRefs } from 'vue';
import { useStore } from 'vuex';
import { cloneDeep, get } from 'lodash';
import { useToast } from 'vue-toastification';
import { DialogProgrammatic } from '@/components/Dialog';
import DataTable from '@/components/table-fields/DataTable.vue';
import ListItemForm from '@/components/modals/ListItemForm.vue'
import Companies from '@/models/Companies';

export default {
  name: 'MeasureUnits',
  props: {
    searchText: {
      type: String,
      default: '',
    },
  },

  components: {
    'data-table': DataTable,
    'list-item-form': ListItemForm,
  },

  setup(props) {
    const store = useStore();
    const toast = useToast();

    const data = reactive({
      tableKey: 0,
      showModal: false,
      itemToEdit: {},
      companyData: null,
    });

    const modalConfig = {
      label: 'Measure Unit',
      showSubCategories: false,
      showColor: false,
      saveFn: async (payload) => {
        const company = await getCompany();
        const name = payload.name.toUpperCase().trim();
        if (company.measureUnits.some((u) => u.name === name)) {
          return { failedData: [{ _errorMsg: `The Measure Unit ${name} is Already Present` }] };
        }
        company.measureUnits.unshift({ name });
        await Companies.updateMeasureUnit(company);
        store.dispatch('getCompanyData');
        return { data: [{ name }] };
      },
      updateFn: async (payload) => {
        const company = await getCompany();
        const name = payload.name.toUpperCase().trim();
        const unit = company.measureUnits.find((u) => u._id === payload._id);
        if (unit) {
          const oldName = unit.name;
          unit.name = name;
          await Companies.updateMeasureUnit(company);
          if (oldName !== name) {
            await Companies.updateItemMeasureUnit({ oldName, newName: name });
          }
          store.dispatch('getCompanyData');
        }
      },
    };

    const getCompany = async () => {
      await store.getters.userPromise;
      const user = store.state.userData;
      return Companies.getOne(user.company);
    };

    const tableOpts = {
      defaultSortField: 'name',
      defaultSortOrder: 'asc',
      stickeyHeaders: false,
      fields: [
        {
          title: 'Name',
          id: 'name',
          prop: 'name',
          name: 'field-generic-input',
          width: '400px',
          visible: true,
          alwaysVisible: true,
          inputProps: {
            type: 'text',
            openCard: false,
          },
        },
        {
          title: 'ACTIONS',
          name: 'table-field-actions',
          id: 'action',
          hideFromChooser: true,
          alwaysVisible: true,
          defaultVisible: true,
          width: '112px',
          permissionModule: 'app&projects',
          permissionKey: 'company_lists',
          inputProps: {
            showEdit: false,
            groupButtons() {
              return [
                {
                  icon: 'icon-edit',
                  event: 'edit',
                  tooltip: 'Edit Measure Unit',
                  permissionModule: 'app&projects',
                  alterDataKey: 'lists',
                },
                {
                  icon: 'icon-bin',
                  event: 'delete',
                  tooltip: 'Delete Measure Unit',
                  permissionModule: 'app&projects',
                  alterDataKey: 'lists',
                },
              ];
            },
          },
        },
      ],
    };

    const loadData = async (params) => {
      const company = await getCompany();
      let units = company.measureUnits || [];
      const search = props.searchText || '';
      if (search) {
        units = units.filter((u) => u.name.toLowerCase().includes(search.toLowerCase()));
      }
      return {
        data: units,
        total: units.length,
      };
    };

    const refreshTable = () => {
      data.tableKey += 1;
    };

    const openModal = (item = {}) => {
      data.itemToEdit = cloneDeep(item);
      data.showModal = true;
    };

    const closeModal = () => {
      data.showModal = false;
      data.itemToEdit = {};
    };

    const onCellClicked = ({ type, data: rowData }) => {
      if (type === 'edit') {
        openModal(rowData);
      } else if (type === 'delete') {
        DialogProgrammatic.confirm({
          title: 'Delete Measure Unit',
          message: `Are you sure you want to delete "${rowData.name}"?`,
          cancelButton: 'Cancel',
          type: 'danger',
          onClose: () => {
            data.isCheckable = false;
            data.selectedRows = [];
          },onConfirm: async () => {
            try {
              const company = await getCompany();
              company.measureUnits = company.measureUnits.filter(
                (u) => u._id !== rowData._id,
              );
              await Companies.updateMeasureUnit(company);
              store.dispatch('getCompanyData');
              toast.success('Measure Unit deleted successfully');
              refreshTable();
            } catch (e) {
              toast.error(get(e, 'data.message') || 'Error deleting Measure Unit');
            }
          },
        });
      }
    };

    return {
      ...toRefs(data),
      modalConfig,
      tableOpts,
      loadData,
      onCellClicked,
      refreshTable,
      openModal,
      closeModal,
    };
  },
};
</script>

<style scoped>
</style>